import React, { useState, useContext, useEffect } from 'react';
import { AdminAuthContext } from '../../context/AdminAuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { UserCog, Lock, Key, Loader2, Home, LogIn, Eye, EyeOff } from 'lucide-react';
import { useAlert } from '../../components/ui/alert';

const AdminAuth = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    secretKey: '',
    nome: '',
    cognome: ''
  });
  const [isRegister, setIsRegister] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showWelcomeContent, setShowWelcomeContent] = useState(false);
  const [isFormChanging, setIsFormChanging] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);

  const { login, register } = useContext(AdminAuthContext);
  const navigate = useNavigate();
  const { showError, showSuccess } = useAlert();

  useEffect(() => {
    setTimeout(() => {
      setIsPageLoaded(true);
      setTimeout(() => {
        setShowWelcomeContent(true);
        setTimeout(() => {
          setShowForm(true);
        }, 300);
      }, 500);
    }, 100);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isRegister) {
        await register(formData.username, formData.password, formData.secretKey, formData.nome, formData.cognome, true); // true per usare MySQL
        showSuccess('Registrazione effettuata con successo');
        setIsRegister(false);
      } else {
        await login(formData.username, formData.password, true); // true per usare MySQL
        handleNavigateWithAnimation('/admin');
      }
    } catch (error) {
      showError(error.message || 'Errore durante l\'autenticazione');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateWithAnimation = (path) => {
    setShowForm(false);
    setTimeout(() => {
      setShowWelcomeContent(false);
      setTimeout(() => {
        setIsPageLoaded(false);
        setTimeout(() => {
          navigate(path);
        }, 500);
      }, 300);
    }, 500);
  };

  const handleFormTypeChange = () => {
    setIsFormChanging(true);
    setTimeout(() => {
      setIsRegister(!isRegister);
      setFormData({ username: '', password: '', secretKey: '', nome: '', cognome: '' });
      setTimeout(() => {
        setIsFormChanging(false);
      }, 300);
    }, 300);
  };

  return (
    <div className="min-h-screen bg-light-surface dark:bg-dark-surface flex">
      {/* Left side - Welcome message */}
      <div className={`
        hidden lg:flex lg:w-1/2
        items-center justify-center p-8
        transition-all duration-500 ease-out
        overflow-hidden
        transform origin-center
        bg-light-surface dark:bg-dark-surface
        before:absolute before:inset-0 before:bg-light-primary/10 dark:before:bg-dark-primary/10
        ${isPageLoaded ? 'scale-y-100' : 'scale-y-0'}
      `}>
        <div className={`
          max-w-md text-center
          transition-all duration-300 ease-out
          ${showWelcomeContent ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}
        `}>
          <UserCog className="w-24 h-24 mx-auto mb-6 text-light-primary dark:text-dark-primary" />
          <h2 className="text-4xl font-bold text-light-on-surface dark:text-dark-on-surface mb-4">
            Pannello Amministratore
          </h2>
          <p className="text-light-on-surface/70 dark:text-dark-on-surface/70 text-lg">
            Accedi per gestire eventi, utenti e configurazioni della piattaforma.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className={`
        w-full lg:w-1/2
        flex items-center justify-center p-8
        transition-all duration-500 ease-out
        transform origin-left
        ${showForm ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full'}
      `}>
        <div className="w-full max-w-md">
          <div className={`
            bg-light-surface-container dark:bg-dark-surface-container 
            rounded-xl shadow-surface p-8
            transition-all duration-300 ease-out
            ${isFormChanging ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}
          `}>
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-2">
                <LogIn className="w-8 h-8 text-light-primary dark:text-dark-primary" />
                <h1 className="text-3xl font-bold text-light-on-surface dark:text-dark-on-surface">
                  {isRegister ? 'Registrazione Admin' : 'Accesso Admin'}
                </h1>
              </div>
              <p className="mt-2 text-light-on-surface/70 dark:text-dark-on-surface/70">
                {isRegister ? 'Crea un nuovo account amministratore' : 'Accedi al pannello di controllo'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {isRegister && (
                <>
                  <div className="relative">
                    <UserCog className="absolute left-3 top-1/2 -translate-y-1/2 text-light-on-surface/50 dark:text-dark-on-surface/50 w-5 h-5" />
                    <input
                      type="text"
                      name="nome"
                      placeholder="Nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      className="w-full p-3 pl-12 bg-light-surface-container-low dark:bg-dark-surface-container-low rounded-lg border border-light-outline dark:border-dark-outline focus:border-light-primary dark:focus:border-dark-primary outline-none text-light-on-surface dark:text-dark-on-surface"
                      required
                    />
                  </div>

                  <div className="relative">
                    <UserCog className="absolute left-3 top-1/2 -translate-y-1/2 text-light-on-surface/50 dark:text-dark-on-surface/50 w-5 h-5" />
                    <input
                      type="text"
                      name="cognome"
                      placeholder="Cognome"
                      value={formData.cognome}
                      onChange={handleInputChange}
                      className="w-full p-3 pl-12 bg-light-surface-container-low dark:bg-dark-surface-container-low rounded-lg border border-light-outline dark:border-dark-outline focus:border-light-primary dark:focus:border-dark-primary outline-none text-light-on-surface dark:text-dark-on-surface"
                      required
                    />
                  </div>
                </>
              )}

              <div className="relative">
                <UserCog className="absolute left-3 top-1/2 -translate-y-1/2 text-light-on-surface/50 dark:text-dark-on-surface/50 w-5 h-5" />
                <input
                  type="text"
                  name="username"
                  placeholder="Username Admin"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="w-full p-3 pl-12 bg-light-surface-container-low dark:bg-dark-surface-container-low rounded-lg border border-light-outline dark:border-dark-outline focus:border-light-primary dark:focus:border-dark-primary outline-none text-light-on-surface dark:text-dark-on-surface"
                  required
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-light-on-surface/50 dark:text-dark-on-surface/50 w-5 h-5" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full p-3 pl-12 pr-12 bg-light-surface-container-low dark:bg-dark-surface-container-low rounded-lg border border-light-outline dark:border-dark-outline focus:border-light-primary dark:focus:border-dark-primary outline-none text-light-on-surface dark:text-dark-on-surface"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-light-on-surface/50 dark:text-dark-on-surface/50 hover:text-light-on-surface dark:hover:text-dark-on-surface transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {isRegister && (
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-light-on-surface/50 dark:text-dark-on-surface/50 w-5 h-5" />
                  <input
                    type={showSecretKey ? "text" : "password"}
                    name="secretKey"
                    placeholder="Chiave Segreta Admin"
                    value={formData.secretKey}
                    onChange={handleInputChange}
                    className="w-full p-3 pl-12 pr-12 bg-light-surface-container-low dark:bg-dark-surface-container-low rounded-lg border border-light-outline dark:border-dark-outline focus:border-light-primary dark:focus:border-dark-primary outline-none text-light-on-surface dark:text-dark-on-surface"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-light-on-surface/50 dark:text-dark-on-surface/50 hover:text-light-on-surface dark:hover:text-dark-on-surface transition-colors"
                  >
                    {showSecretKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              )}

              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary rounded-lg hover:bg-light-primary-container dark:hover:bg-dark-primary-container transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{isRegister ? 'Registrazione...' : 'Accesso...'}</span>
                    </>
                  ) : (
                    <span>{isRegister ? 'Registrati' : 'Accedi'}</span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleFormTypeChange}
                  className="w-full py-3 px-4 bg-transparent border border-light-outline dark:border-dark-outline text-light-on-surface dark:text-dark-on-surface rounded-lg hover:bg-light-surface-container-low dark:hover:bg-dark-surface-container-low transition-colors"
                >
                  {isRegister ? 'Passa al Login' : 'Registra Nuovo Admin'}
                </button>
                {/* Home button */}
                <Link
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigateWithAnimation('/');
                  }}
                  to="/"
                  className="w-full py-3 px-4 bg-transparent border border-light-outline dark:border-dark-outline text-light-on-surface dark:text-dark-on-surface rounded-lg hover:bg-light-surface-container-low dark:hover:bg-dark-surface-container-low transition-colors flex items-center justify-center gap-2"
                >
                  <Home className="w-5 h-5" />
                  <span>Torna alla Home</span>
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAuth;